import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { MathTextBlock } from '../../components/MathTextBlock';
import { factoryService } from '../../services/factoryService';
import { figureKindLabelZh } from '../utils/extractQaPreview';
import { cropPageImageByBox } from '../utils/figureCropUtils';

export type FigureCropPreview = {
  unitId: string;
  qaIndex: number;
  questionPreview: string;
  figureKind?: string;
  figureNote?: string;
  figureBox?: { x: number; y: number; w: number; h: number };
};

type Props = {
  batchId: string;
  preview: FigureCropPreview;
  onClose: () => void;
  /** 带插图颗粒导航：当前在序列中的位置（0-based） */
  navIndex?: number;
  navTotal?: number;
  onPrevFigure?: () => void;
  onNextFigure?: () => void;
};

/** Desk preview: crop page JPEG by normalized figure_box and show in a dialog. */
export function FigureCropPreviewDialog({
  batchId,
  preview,
  onClose,
  navIndex,
  navTotal,
  onPrevFigure,
  onNextFigure,
}: Props) {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const [dataUrl, setDataUrl] = useState('');
  const [meta, setMeta] = useState<{ pageW: number; pageH: number; crop: { x: number; y: number; w: number; h: number } } | null>(null);

  const box = preview.figureBox;
  const hasBox = Boolean(box && (box.w > 0 || box.h > 0));
  const hasNav = typeof navIndex === 'number'
    && typeof navTotal === 'number'
    && navTotal > 0
    && (Boolean(onPrevFigure) || Boolean(onNextFigure));
  const canPrev = hasNav && navIndex! > 0 && Boolean(onPrevFigure);
  const canNext = hasNav && navIndex! < navTotal! - 1 && Boolean(onNextFigure);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError('');
    setDataUrl('');
    setMeta(null);

    if (!hasBox || !box) {
      setStatus('error');
      setError('提取结果没有可用的 figure_box（坐标为空）');
      return;
    }

    const url = factoryService.structuralUnitPageImageUrl(batchId, preview.unitId);
    void cropPageImageByBox(url, box)
      .then((out) => {
        if (cancelled) return;
        setDataUrl(out.dataUrl);
        setMeta({ pageW: out.pageW, pageH: out.pageH, crop: out.crop });
        setStatus('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus('error');
        setError(err instanceof Error ? err.message : String(err));
      });

    return () => {
      cancelled = true;
    };
  }, [batchId, preview.unitId, hasBox, box?.x, box?.y, box?.w, box?.h]);

  useEffect(() => {
    if (!hasNav) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && canPrev) {
        e.preventDefault();
        onPrevFigure?.();
      } else if (e.key === 'ArrowRight' && canNext) {
        e.preventDefault();
        onNextFigure?.();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasNav, canPrev, canNext, onPrevFigure, onNextFigure, onClose]);

  const kindZh = figureKindLabelZh(preview.figureKind) || preview.figureKind || '—';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="题图切块预览"
        className="compare-figure-crop-dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="compare-figure-crop-dialog-head">
          <div className="min-w-0">
            <div className="compare-figure-crop-dialog-kicker">
              Q{preview.qaIndex + 1} · 题图切块预览 · {kindZh}
              {hasNav ? (
                <span className="compare-figure-crop-dialog-nav-count">
                  {' · '}插图 {navIndex! + 1}/{navTotal}
                </span>
              ) : null}
            </div>
            <div className="compare-figure-crop-dialog-title" title={preview.questionPreview}>
              {preview.questionPreview
                ? <MathTextBlock text={preview.questionPreview} className="text-sm" />
                : '（无题干）'}
            </div>
          </div>
          <button type="button" className="compare-figure-crop-dialog-close" onClick={onClose}>
            关闭
          </button>
        </div>

        <div className="compare-figure-crop-dialog-meta">
          {preview.figureNote ? <div>备注：{preview.figureNote}</div> : null}
          {box ? (
            <div className="compare-figure-crop-dialog-coords">
              归一化坐标：x={box.x.toFixed(3)} y={box.y.toFixed(3)} w={box.w.toFixed(3)} h={box.h.toFixed(3)}
              {meta
                ? ` · 像素 ${meta.crop.w}×${meta.crop.h} @ (${meta.crop.x},${meta.crop.y}) / 页 ${meta.pageW}×${meta.pageH}`
                : ''}
            </div>
          ) : (
            <div className="compare-figure-crop-dialog-coords">无 figure_box</div>
          )}
        </div>

        <div className="compare-figure-crop-dialog-body">
          {status === 'loading' ? (
            <div className="compare-figure-crop-dialog-status">正在按坐标切页图…</div>
          ) : null}
          {status === 'error' ? (
            <div className="compare-figure-crop-dialog-status is-error">{error || '切图失败'}</div>
          ) : null}
          {status === 'ready' && dataUrl ? (
            <img src={dataUrl} alt="题图切块" className="compare-figure-crop-dialog-img" />
          ) : null}
        </div>

        {hasNav ? (
          <div className="compare-figure-crop-dialog-nav">
            <button
              type="button"
              className="compare-figure-crop-dialog-nav-btn"
              disabled={!canPrev}
              onClick={() => onPrevFigure?.()}
            >
              <ChevronLeft size={16} strokeWidth={2.2} aria-hidden />
              上一带插图颗粒
            </button>
            <button
              type="button"
              className="compare-figure-crop-dialog-nav-btn"
              disabled={!canNext}
              onClick={() => onNextFigure?.()}
            >
              下一带插图颗粒
              <ChevronRight size={16} strokeWidth={2.2} aria-hidden />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
